import { ctaDesc, ctaTitle } from "@/lib/seo";
// /checklist/$visatype/$scenario — leaf checklist page
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getVisaType } from "@/data/visa-nationalities";
import { CATEGORIES } from "@/data/visa-types";
import { SCENARIOS, getChecklist, getScenario } from "@/data/visa-checklists";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { TrustLayer } from "@/components/TrustLayer";
import { ContactCTA } from "@/components/seo/Blocks";
import { Check, AlertTriangle, Clock, Receipt, BadgeCheck, ChevronRight, Phone, MessageCircle, FileCheck2 } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/checklist/$visatype/$scenario")({
  loader: ({ params }) => {
    const v = getVisaType(params.visatype);
    const s = getScenario(params.scenario);
    const ck = getChecklist(params.visatype, params.scenario);
    if (!v || !s || !ck) throw notFound();
    return { vt: params.visatype, sc: params.scenario };
  },
  head: ({ params }) => {
    const v = getVisaType(params.visatype);
    const s = getScenario(params.scenario);
    const ck = getChecklist(params.visatype, params.scenario);
    if (!v || !s || !ck) return { meta: [{ title: ctaTitle("ไม่พบ Checklist", "/checklist/$visatype/$scenario", "th")}] };
    const url = `${SITE}/checklist/${v.id}/${s.slug}`;
    const title = `Checklist ${v.nameTh} · ${s.shortTh} (${ck.items.length} รายการ) | NYC`;
    const desc = `เอกสารครบสำหรับ${s.nameTh}ของ${v.nameTh} (${v.code}): ${ck.items.slice(0, 3).map((i) => i.label.split("(")[0].trim).join(" · ")}… — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    return {
      meta: [
        { title: ctaTitle(title, "/checklist/$visatype/$scenario", "th") }, { name: "description", content: ctaDesc(desc, "/checklist/$visatype/$scenario", "th")},
        { property: "og:title", content: ctaTitle(title, "/checklist/$visatype/$scenario", "th")}, { property: "og:description", content: ctaDesc(desc, "/checklist/$visatype/$scenario", "th")},
        { property: "og:url", content: url },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"
        ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "HowTo", "@id": `${url}#howto`,
              name: `${s.nameTh} — ${v.nameTh}`, description: s.description,
              estimatedCost: { "@type": "MonetaryAmount", currency: "THB", value: ck.govFee },
              supply: ck.items.map((i) => ({ "@type": "HowToSupply", name: i.label })),
              step: [
                { "@type": "HowToStep", position: 1, name: "เตรียมเอกสาร", text: `รวบรวมเอกสาร ${ck.items.length} รายการตาม Checklist ด้านล่าง` },
                { "@type": "HowToStep", position: 2, name: "ส่งให้ทีมตรวจ", text: "ส่งภาพเอกสารผ่าน LINE @nycli ทีมตรวจสอบความครบถ้วนภายใน 1 ชั่วโมง" },
                { "@type": "HowToStep", position: 3, name: "ยื่นที่หน่วยงาน", text: `ยื่นที่ ตม. / สถานทูต / BOI — รอผล ${ck.processingDays}` },
                { "@type": "HowToStep", position: 4, name: "รับวีซ่า", text: `รับ ${v.nameTh} (${v.code}) อายุ ${v.durationInitial}` },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `${v.nameTh} - ${s.shortTh}`, serviceType: "Visa Document Preparation",
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999" },
              offers: { "@type": "Offer", price: ck.serviceFee.replace(/[^\d]/g, "").slice(0, 6) || "20000", priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "Visa Checklist", item: `${SITE}/checklist` },
                { "@type": "ListItem", position: 3, name: v.nameTh, item: `${SITE}/checklist/${v.id}` },
                { "@type": "ListItem", position: 4, name: s.shortTh, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) }))
        ]),
      ],
    };
  },
  component: ChecklistLeaf,
});

function ChecklistLeaf() {
  const { vt, sc } = Route.useLoaderData();
  const v = getVisaType(vt)!;
  const s = getScenario(sc)!;
  const ck = getChecklist(vt, sc)!;
  const cat = CATEGORIES[v.category];

  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep text-white">
        <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 sm:py-14">
          <nav aria-label="Breadcrumb" className="mb-3 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <ChevronRight className="mx-1 inline h-3 w-3 text-white/30" />
            <Link to="/checklist" className="hover:text-gold">Checklist</Link>
            <ChevronRight className="mx-1 inline h-3 w-3 text-white/30" />
            <Link to="/checklist/$visatype" params={{ visatype: v.id }} className="hover:text-gold">{v.nameTh}</Link>
            <ChevronRight className="mx-1 inline h-3 w-3 text-white/30" />
            <span className="text-white/85">{s.shortTh}</span>
          </nav>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${cat.color}`}>{cat.emoji} {cat.label}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-bold text-white/90">{s.emoji} {s.shortTh}</span>
          </div>
          <h1 className="aeo-speakable mt-3 font-display text-2xl font-extrabold leading-tight sm:text-4xl">
            Checklist <span className="text-gold">{v.nameTh}</span> — {s.nameTh}
          </h1>
          <p className="mt-3 max-w-3xl text-base text-white/85">{s.description}</p>

          <dl className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-white/10 px-4 py-3">
              <dt className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-white/80"><Clock className="h-3 w-3" /> ระยะเวลา</dt>
              <dd className="mt-1 text-sm font-bold text-white">{ck.processingDays}</dd>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3">
              <dt className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-white/80"><Receipt className="h-3 w-3" /> ค่าราชการ</dt>
              <dd className="mt-1 text-sm font-bold text-white">{ck.govFee}</dd>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3">
              <dt className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-white/80"><BadgeCheck className="h-3 w-3" /> ค่าบริการ</dt>
              <dd className="mt-1 text-sm font-bold text-gold">{ck.serviceFee}</dd>
            </div>
          </dl>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
        <h2 className="aeo-speakable mb-5 font-display text-2xl font-extrabold text-navy-deep">รายการเอกสาร ({ck.items.length} รายการ)</h2>
        <ol className="space-y-2">
          {ck.items.map((it, i) => (
            <li key={i} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <div className={`mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full ${it.required ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                <Check className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">{it.label}</div>
                {it.notes && <div className="mt-1 text-[12px] text-slate-500">{it.notes}</div>}
                {!it.required && <div className="mt-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">Optional</div>}
              </div>
            </li>
          ))}
        </ol>

        {ck.warnings.length > 0 && (
          <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <h3 className="flex items-center gap-2 font-display text-base font-extrabold text-amber-900">
              <AlertTriangle className="h-4 w-4" /> ข้อควรระวัง
            </h3>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-[13px] text-amber-900">
              {ck.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        <section className="mt-12">
          <h3 className="mb-4 font-display text-xl font-extrabold text-navy-deep">สถานการณ์อื่นของ {v.nameTh}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {SCENARIOS.filter((x) => x.slug !== s.slug).map((other) => (
              <Link
                key={other.slug}
                to="/checklist/$visatype/$scenario"
                params={{ visatype: v.id, scenario: other.slug }}
                className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 transition hover:border-gold/50 hover:shadow-sm"
              >
                <span className="text-xl">{other.emoji}</span>
                <span className="flex-1 text-sm font-semibold text-slate-700 group-hover:text-amber-600">{other.nameTh}</span>
                <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-amber-600" />
              </Link>
            ))}
          </div>
        </section>

        <div className="mt-10 flex flex-wrap gap-3">
          <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
            <MessageCircle className="h-4 w-4" /> ส่งเอกสารให้ทีมตรวจฟรี
          </a>
          <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
            <Phone className="h-4 w-4" /> 083-249-4999
          </a>
        </div>
      </div>

      <ContactCTA />
      <TrustLayer variant="compact" />
    </main>
  );
}
